import { Bolt } from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { EChatAgentMessageAuthor, type IChatAgentChat, type IChatAgentMessage } from '@sharedInterfaces/ChatAgent';
import type { AppState } from '@store/store';
import { FileChartColumnIncreasing, GraduationCap, Send, Sparkles, Target, Users } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';

import type { Shortcut } from './types';

const getShortcutIcon = (iconName?: string) => {
    const iconSize = 14;
    switch (iconName) {
        case 'target':
            return <Target size={iconSize} />;
        case 'sparkles':
            return <Sparkles size={iconSize} />;
        case 'graduation-cap':
            return <GraduationCap size={iconSize} />;
        case 'users':
            return <Users size={iconSize} />;
        case 'file-chart-column-increasing':
            return <FileChartColumnIncreasing size={iconSize} />;
        default:
            return <Target size={iconSize} />;
    }
};

interface MobileChatPanelProps {
    messages: IChatAgentMessage[];
    input: string;
    setInput: (input: string) => void;
    sendMessage: () => void;
    handleKeyPress: (e: React.KeyboardEvent) => void;
    isTyping: boolean;
    chat: IChatAgentChat | null;
    scrollRef: React.RefObject<HTMLDivElement>;
    inputRef: React.RefObject<HTMLInputElement>;
    renderFormattedText: (text: string, isUserMessage?: boolean) => React.ReactNode;
    langStrings: any;
    shortcuts: Shortcut[];
    highlightedShortcut: Shortcut;
    handleShortcutAction: (action: string) => void;
    isAgentAction?: boolean;
}

export const MobileChatPanel: React.FC<MobileChatPanelProps> = ({
    messages,
    input,
    setInput,
    sendMessage,
    handleKeyPress,
    isTyping,
    chat,
    scrollRef,
    inputRef,
    renderFormattedText,
    langStrings,
    shortcuts,
    highlightedShortcut,
    handleShortcutAction,
    isAgentAction = false,
}) => {
    const theme = useTheme();
    const employee = useSelector((state: AppState) => state.employee);

    const getButtonStyles = () => ({
        height: 28,
        minHeight: 28,
        fontSize: '0.75rem',
        fontWeight: 500,
        padding: theme.spacing(0.25, 1),
        lineHeight: 1.2,
        borderRadius: 3,
        textTransform: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            transform: 'none',
        },
        '&:active': {
            transform: 'scale(0.98)',
            bgcolor: alpha(theme.palette.primary.main, 0.15),
        },
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Chat Messages */}
            <Box
                ref={scrollRef}
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    p: 0.75,
                    pl: 0,
                    mb: 2,
                }}
            >
                {messages.length === 0 ? (
                    <Box sx={{ display: 'none' }}>{/* Leerer Box, wenn keine Nachrichten vorhanden sind */}</Box>
                ) : (
                    messages.map((msg, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                flexDirection: msg.role === EChatAgentMessageAuthor.USER ? 'row-reverse' : 'row',
                                mb: 1,
                                maxWidth: '100%',
                            }}
                        >
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 1.5,
                                    maxWidth: '90%',
                                    bgcolor:
                                        msg.role === EChatAgentMessageAuthor.USER
                                            ? alpha(theme.palette.primary.main, 0.8)
                                            : theme.palette.background.paper,
                                    color:
                                        msg.role === EChatAgentMessageAuthor.USER
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.primary,
                                    borderRadius: 2,
                                    borderTopLeftRadius: msg.role !== EChatAgentMessageAuthor.USER ? 0 : 2,
                                    borderTopRightRadius: msg.role === EChatAgentMessageAuthor.USER ? 0 : 2,
                                }}
                            >
                                {renderFormattedText(msg.text, msg.role === EChatAgentMessageAuthor.USER)}
                            </Paper>
                        </Box>
                    ))
                )}

                {/* Typing indicator */}
                {isTyping && (
                    <Box
                        sx={{
                            display: 'flex',
                            mb: 1,
                        }}
                    >
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                maxWidth: '100%',
                                bgcolor: theme.palette.background.paper,
                                borderRadius: 2,
                                borderTopLeftRadius: 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={14} sx={{ mr: 0.75 }} />
                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }} color="text.primary">
                                    {'Schreibt...'}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* Chat Input */}
            <Box sx={{ mt: 'auto', position: 'sticky', bottom: 0 }}>
                <TextField
                    inputRef={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    multiline
                    rows={4}
                    maxRows={4}
                    fullWidth
                    placeholder={langStrings.inputPlaceholder || 'Was kann ich heute für dich tun?'}
                    variant="outlined"
                    disabled={false}
                    sx={{
                        mr: 0,
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            bgcolor: alpha(theme.palette.background.paper, 0.95),
                        },
                    }}
                    InputProps={{
                        startAdornment: isAgentAction ? (
                            <InputAdornment position="start">
                                <Bolt color="primary" fontSize="small" />
                            </InputAdornment>
                        ) : null,
                        endAdornment: (
                            <IconButton
                                onClick={sendMessage}
                                disabled={!input.trim()}
                                size="small"
                                sx={{
                                    color: theme.palette.primary.main,
                                    '&.Mui-disabled': {
                                        color: alpha(theme.palette.primary.main, 0.3),
                                    },
                                }}
                            >
                                <Send size={16} />
                            </IconButton>
                        ),
                    }}
                />

                {/* Quick Actions / Shortcuts */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        overflowX: 'auto',
                        overflowY: 'visible',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 1.5,
                        px: 0.5,
                        pb: 0.5,
                        maxWidth: '100%',
                        minHeight: 36,
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {shortcuts.map((shortcut, index) => (
                        <Button
                            key={index}
                            variant="animatedBorder"
                            onClick={() => handleShortcutAction(shortcut.action)}
                            size="small"
                            startIcon={getShortcutIcon(shortcut.icon)}
                            onMouseMove={(e: React.MouseEvent<HTMLButtonElement>) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = ((e.clientX - rect.left) / rect.width) * 100;
                                const y = ((e.clientY - rect.top) / rect.height) * 100;
                                e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
                                e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
                            }}
                            sx={{ flexShrink: 0 }}
                        >
                            {shortcut.text}
                        </Button>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default MobileChatPanel;
